import { useState } from "react";
import { presalesApi } from "../../api/presalesApi";

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${d.getDate().toString().padStart(2, "0")} ${d.toLocaleString("en", { month: "short" })}`;
}

function pctOff(list, actual) {
  if (!list || !actual || actual >= list) return null;
  return (((list - actual) / list) * 100).toFixed(1);
}

const KINDS = ["offer", "counter", "accepted", "rejected"];

function NegModal({ leadId, onSave, onClose }) {
  const [form, setForm] = useState({
    entry_date: new Date().toISOString().slice(0, 10),
    party_name: "",
    kind: "offer",
    note: "",
    payment_term: "",
  });
  const [saving, setSaving] = useState(false);

  async function submit() {
    if (!form.entry_date || !form.kind) return;
    setSaving(true);
    try {
      await presalesApi.addNegEntry(leadId, form);
      onSave();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="ps-modal-scrim" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="ps-modal">
        <div className="ps-modal-hd">
          <h3>Add Negotiation Entry</h3>
          <button className="ps-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="ps-modal-body">
          <div className="ps-form-row two">
            <div className="ps-fld">
              <label>Date</label>
              <input type="date" value={form.entry_date}
                onChange={(e) => setForm(p => ({ ...p, entry_date: e.target.value }))} />
            </div>
            <div className="ps-fld">
              <label>Party</label>
              <input type="text" value={form.party_name} placeholder="Name…"
                onChange={(e) => setForm(p => ({ ...p, party_name: e.target.value }))} />
            </div>
          </div>
          <div className="ps-fld">
            <label>Kind</label>
            <div className="ps-kind-seg">
              {KINDS.map(k => (
                <button
                  key={k}
                  className={`${form.kind === k ? "on " + k : ""}`}
                  onClick={() => setForm(p => ({ ...p, kind: k }))}
                >
                  {k}
                </button>
              ))}
            </div>
          </div>
          <div className="ps-fld">
            <label>Note</label>
            <textarea value={form.note} placeholder="Summary…"
              onChange={(e) => setForm(p => ({ ...p, note: e.target.value }))} />
          </div>
          <div className="ps-fld">
            <label>Payment Term</label>
            <input type="text" value={form.payment_term} placeholder="e.g. Net-30, Annual upfront…"
              onChange={(e) => setForm(p => ({ ...p, payment_term: e.target.value }))} />
          </div>
        </div>
        <div className="ps-modal-ft">
          <button onClick={onClose}>Cancel</button>
          <button className="primary" disabled={saving || !form.entry_date} onClick={submit}>
            {saving ? "Saving…" : "Add Entry"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PricingTab({ lead, mutate, showToast }) {
  const pricing = lead.pricing || { list_price: 0, offered_price: 0, agreed_price: 0 };

  const [listRs, setListRs]     = useState(String(pricing.list_price / 100 || ""));
  const [offeredRs, setOfferedRs] = useState(String(pricing.offered_price / 100 || ""));
  const [agreedRs, setAgreedRs]  = useState(String(pricing.agreed_price / 100 || ""));
  const [savingPrice, setSavingPrice] = useState(false);
  const [showNegModal, setShowNegModal] = useState(false);

  const [termsDraft, setTermsDraft] = useState(null);
  const [savingTerms, setSavingTerms] = useState(false);

  const listP    = Math.round(parseFloat(listRs || 0) * 100);
  const offeredP = Math.round(parseFloat(offeredRs || 0) * 100);
  const agreedP  = Math.round(parseFloat(agreedRs || 0) * 100);

  async function savePricing() {
    setSavingPrice(true);
    try {
      await presalesApi.upsertPricing(lead.id, {
        list_price: listP, offered_price: offeredP, agreed_price: agreedP,
      });
      await mutate();
      showToast?.("Pricing saved", "success");
    } catch (err) {
      showToast?.(err.message || "Failed to save pricing", "error");
    } finally {
      setSavingPrice(false);
    }
  }

  async function deleteNeg(entryId) {
    try {
      await presalesApi.deleteNegEntry(entryId);
      await mutate();
    } catch (err) {
      showToast?.(err.message || "Failed to delete entry", "error");
    }
  }

  async function saveTerms() {
    setSavingTerms(true);
    try {
      await presalesApi.updateLead(lead.id, { terms: termsDraft });
      setTermsDraft(null);
      await mutate();
      showToast?.("Terms saved", "success");
    } catch (err) {
      showToast?.(err.message || "Failed to save terms", "error");
    } finally {
      setSavingTerms(false);
    }
  }

  const discOff = pctOff(listP, offeredP);
  const discAg  = pctOff(listP, agreedP);
  const negotiation = lead.negotiation || [];

  return (
    <div>
      {/* Pricing inputs */}
      <div className="ps-so-section">
        <div className="ps-so-lbl">Pricing (₹)</div>
        <div className="ps-price-grid">
          <div className="ps-price-fld">
            <span className="k">List Price</span>
            <input type="number" value={listRs}
              onChange={(e) => setListRs(e.target.value)} />
          </div>
          <div className="ps-price-fld">
            <span className="k">Offered Price</span>
            <input type="number" value={offeredRs}
              onChange={(e) => setOfferedRs(e.target.value)} />
          </div>
          <div className="ps-price-fld agreed">
            <span className="k">Agreed Price</span>
            <input type="number" value={agreedRs}
              onChange={(e) => setAgreedRs(e.target.value)} />
          </div>
        </div>
        <div className="ps-discount">
          {discOff ? (
            <div className="dk">
              <span className="k">Offered discount</span>
              <span className="v">{discOff}%</span>
            </div>
          ) : <div className="dk dk-empty" />}
          {discAg ? (
            <div className="dk">
              <span className="k">Agreed discount</span>
              <span className="v">{discAg}%</span>
            </div>
          ) : <div className="dk dk-empty" />}
          <div className="ps-price-save">
            <button disabled={savingPrice} onClick={savePricing}>
              {savingPrice ? "Saving…" : "Save Pricing"}
            </button>
          </div>
        </div>
      </div>

      {/* Negotiation log */}
      <div className="ps-so-section">
        <div className="ps-so-lbl">
          <span>Negotiation Log</span>
          <button onClick={() => setShowNegModal(true)}>+ Add Entry</button>
        </div>
        {negotiation.length === 0 ? (
          <div className="ps-so-empty">No entries yet</div>
        ) : (
          <div>
            {negotiation.map((e) => (
              <div key={e.id} className="ps-neg-row">
                <span className="ps-neg-when">{fmtDate(e.entry_date)}</span>
                <div className="ps-neg-body">
                  <div className="ps-neg-head">
                    {e.party_name && <span className="ps-neg-who">{e.party_name}</span>}
                    <span className={`ps-neg-tag ${e.kind}`}>{e.kind}</span>
                    <button className="ps-neg-del" onClick={() => deleteNeg(e.id)} title="Remove">×</button>
                  </div>
                  {e.note && <div className="ps-neg-note">{e.note}</div>}
                  {e.payment_term && <div className="ps-neg-term">{e.payment_term}</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Terms */}
      <div className="ps-so-section">
        <div className="ps-so-lbl">Payment Terms</div>
        <textarea
          className="ps-notes-area"
          value={termsDraft !== null ? termsDraft : (lead.terms || "")}
          onChange={(e) => setTermsDraft(e.target.value)}
          placeholder="Enter payment terms…"
        />
        {termsDraft !== null && (
          <div className="ps-notes-save-bar">
            <button onClick={() => setTermsDraft(null)}>Cancel</button>
            <button className="primary" disabled={savingTerms} onClick={saveTerms}>
              {savingTerms ? "Saving…" : "Save"}
            </button>
          </div>
        )}
      </div>

      {showNegModal && (
        <NegModal
          leadId={lead.id}
          onSave={async () => { setShowNegModal(false); await mutate(); showToast?.("Entry added", "success"); }}
          onClose={() => setShowNegModal(false)}
        />
      )}
    </div>
  );
}
